import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Wrench, Paintbrush, Box, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProjectCard from "@/components/ProjectCard";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_hot-rod-coatings/artifacts/1qfh9b4z_Hero%20Image%20-%20Martens%20Motor.jpg";

// Simplified animation variants for better performance
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function HomePage() {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        await axios.post(`${API}/seed-projects`);
        const response = await axios.get(`${API}/projects/featured`);
        setFeaturedProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Hot Rod"
            className="w-full h-full object-cover"
            loading="eager" />

          <div className="absolute inset-0 hero-overlay" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl">

            <span className="font-mono text-xs tracking-[0.3em] text-amber-500 uppercase mb-4 block">
              CUSTOM BUILDS · SPECIALTY COATINGS · 3D DESIGN & PARTS FAB
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white tracking-tighter leading-none mb-6">
              BUILDING<br />
              <span className="text-amber-500">LEGENDS</span>
            </h1>
            <p className="font-body text-lg md:text-xl text-zinc-300 mb-8 max-w-xl leading-relaxed">Where passion fuels every detail. We bring classic machines back to life with modern performance and timeless style.

            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/gallery">
                <Button
                  className="bg-amber-500 text-black font-heading text-lg tracking-wide px-8 py-6 rounded-none hover:bg-amber-400 transition-all duration-200 hover:-translate-y-1"
                  data-testid="view-work-btn">

                  VIEW OUR WORK
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/quote">
                <Button
                  variant="outline"
                  className="border-amber-500 text-amber-500 font-heading text-lg tracking-wide px-8 py-6 rounded-none hover:bg-amber-500/10 transition-all duration-200"
                  data-testid="start-project-btn">

                  START YOUR PROJECT
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-amber-500 rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-zinc-950" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16">

            <span className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase">
              WHAT WE DO
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mt-2">
              OUR <span className="text-amber-500">EXPERTISE</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {/* Custom Builds */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-zinc-950 p-8 md:p-12 group">

              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-navy flex items-center justify-center flex-shrink-0">
                  <Wrench size={32} className="text-amber-500" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl sm:text-3xl text-white mb-4 tracking-tight">
                    CUSTOM BUILDS
                  </h3>
                  <p className="font-body text-zinc-400 leading-relaxed mb-6">
                    From ground-up custom builds to complete transformations. We specialize in bringing your vision to life with precision craftsmanship and attention to detail.
                  </p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 font-mono text-sm text-amber-500 hover:text-amber-400 transition-colors">

                    LEARN MORE <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Specialty Coatings */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-zinc-950 p-8 md:p-12 group">

              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-navy flex items-center justify-center flex-shrink-0">
                  <Paintbrush size={32} className="text-amber-500" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl sm:text-3xl text-white mb-4 tracking-tight">
                    SPECIALTY COATINGS
                  </h3>
                  <p className="font-body text-zinc-400 leading-relaxed mb-6">Cerakote® coatings and custom paint work. Our coatings division delivers show-quality finishes with industrial-grade durability.
We work with you to consider temperature and heat cycles and the coating that will suit all pieces best so that finishes will look good for years to come. 
                  </p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 font-mono text-sm text-amber-500 hover:text-amber-400 transition-colors">

                    LEARN MORE <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* 3D Design & Parts Fabrication */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-zinc-950 p-8 md:p-12 group">

              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-navy flex items-center justify-center flex-shrink-0">
                  <Box size={32} className="text-amber-500" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl sm:text-3xl text-white mb-4 tracking-tight">
                    3D DESIGN & PARTS FAB
                  </h3>
                  <p className="font-body text-zinc-400 leading-relaxed mb-6">
                    From sketch or scan to finished part. Powered by our Revopoint MetroX blue-laser 3D scanner, we design and fabricate custom components engineered to fit and built to last.
                  </p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 font-mono text-sm text-amber-500 hover:text-amber-400 transition-colors">

                    LEARN MORE <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-24 bg-[#09090b]" data-testid="featured-projects">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12">

            <div>
              <span className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase">
                RECENT BUILDS
              </span>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mt-2">
                FEATURED <span className="text-amber-500">PROJECTS</span>
              </h2>
            </div>
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 font-mono text-sm text-amber-500 hover:text-amber-400 transition-colors mt-4 sm:mt-0">

              VIEW ALL PROJECTS <ArrowRight size={16} />
            </Link>
          </motion.div>

          {loading ?
          <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) =>
            <div key={i} className="aspect-[4/3] bg-zinc-900 animate-pulse" />
            )}
            </div> :

          <div className="grid md:grid-cols-3 gap-6">
              {featuredProjects.slice(0, 3).map((project, index) =>
            <ProjectCard key={project.id} project={project} index={index} />
            )}
            </div>
          }
        </div>
      </section>

      {/* Instagram Section */}
      <section className="py-24 bg-zinc-950 border-t border-white/10" data-testid="instagram-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12">

            <div className="flex items-center justify-center gap-3 mb-4">
              <Instagram size={24} className="text-amber-500" />
              <span className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase">
                @MARTENSMOTORCO
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight">
              FOLLOW THE <span className="text-amber-500">BUILD</span>
            </h2>
          </motion.div>

          {/* Instagram Feed Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-w-5xl mx-auto">
            {/* Large featured image - spans 2 cols and 2 rows on md+ */}
            <button
              onClick={() => window.open('https://www.instagram.com/p/DOw1E74gQoo/', '_blank', 'noopener,noreferrer')}
              className="col-span-2 md:row-span-2 relative group overflow-hidden cursor-pointer aspect-square">

              <img
                src="https://customer-assets.emergentagent.com/job_hot-rod-coatings/artifacts/6ljhb10a_Insta%20gQoo.png"
                alt="Gray and black truck with red stripe"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />

              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <Instagram size={40} className="text-white" />
              </div>
            </button>
            {/* Small images */}
            <button
              onClick={() => window.open('https://www.instagram.com/p/DLIlQv3SqFw/', '_blank', 'noopener,noreferrer')}
              className="relative group overflow-hidden cursor-pointer aspect-square">

              <img
                src="https://customer-assets.emergentagent.com/job_hot-rod-coatings/artifacts/kxqw84c1_Insta%20SqFw.png"
                alt="Orange car interior"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />

              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <Instagram size={24} className="text-white" />
              </div>
            </button>
            <button
              onClick={() => window.open('https://instagram.com/martensmotorco', '_blank', 'noopener,noreferrer')}
              className="relative group overflow-hidden cursor-pointer aspect-square">

              <img
                src="https://customer-assets.emergentagent.com/job_hot-rod-coatings/artifacts/3xc9j1ut_Insta%20kTVG.png"
                alt="Cerakote workbench with exhaust headers"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />

              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <Instagram size={24} className="text-white" />
              </div>
            </button>
            <button
              onClick={() => window.open('https://www.instagram.com/p/DKxW7sJy25C/?img_index=1', '_blank', 'noopener,noreferrer')}
              className="relative group overflow-hidden cursor-pointer aspect-square">

              <img
                src="https://customer-assets.emergentagent.com/job_hot-rod-coatings/artifacts/gt3dyxl4_Insta%20y25C.png"
                alt="Blue Chevy Square Body Truck"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />

              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <Instagram size={24} className="text-white" />
              </div>
            </button>
            <button
              onClick={() => window.open('https://instagram.com/martensmotorco', '_blank', 'noopener,noreferrer')}
              className="relative group overflow-hidden cursor-pointer aspect-square">

              <img
                src="https://customer-assets.emergentagent.com/job_hot-rod-coatings/artifacts/zdlrc84e_Insta%20kSAJ.png"
                alt="Vintage red gas can"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />

              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <Instagram size={24} className="text-white" />
              </div>
            </button>
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => window.open('https://instagram.com/martensmotorco', '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center gap-2 font-mono text-sm text-amber-500 hover:text-amber-400 transition-colors">

              FOLLOW US ON INSTAGRAM <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-navy relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+CjxyZWN0IHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgZmlsbD0ibm9uZSIvPgo8cGF0aCBkPSJNMzAgMEwzMCA2MCIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjAuNSIvPgo8cGF0aCBkPSJNMCAzMEw2MCAzMCIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjAuNSIvPgo8L3N2Zz4K')]" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-6">
              READY TO START<br />
              <span className="text-amber-500">YOUR BUILD?</span>
            </h2>
            <p className="font-body text-lg text-zinc-300 mb-8 max-w-2xl mx-auto">
              Whether you're restoring a classic or need specialty coating services, we're here to bring your vision to life.
            </p>
            <Link to="/quote">
              <Button
                className="bg-amber-500 text-black font-heading text-xl tracking-wide px-10 py-6 rounded-none hover:bg-amber-400 transition-all duration-200 hover:-translate-y-1"
                data-testid="cta-quote-btn">

                LET'S GET STARTED
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>);

}